/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_designations-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_designations-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_designations-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, code, kind
 * - Sortable fields: sort_order, created_at, updated_at
 * - Required fields: id, code, name, kind, sort_order, archived, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_designations-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_designations-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_designations.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_designations-entity-config.ts
 * Public fields: 8/8 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupDesignationsClient, LookupDesignationsQueryOptions } from '../generated_clients/generated-lookup_designations-api-client.js';
import {
  LookupDesignationsInsert,
  LookupDesignationsUpdate,
  LookupDesignationsFieldConfig
} from '../../types/generated_schemas/generated-lookup_designations-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_designations_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupDesignationsKeys = {
  all: ['lookup_designations'] as const,
  lists: () => [...lookupDesignationsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupDesignationsQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupDesignationsKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupDesignationsKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupDesignationsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupDesignationsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupDesignationsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupDesignationsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupDesignationsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupDesignationsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupDesignationsClient();
};

/**
 * Hook to get all lookup_designations with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_designations data
 */
export const useLookupDesignationsList = (options?: LookupDesignationsQueryOptions) => {
  const client = useLookupDesignationsClient();
  
  return useQuery({
    queryKey: lookupDesignationsKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_designation by ID
 *
 * @param id - The lookup_designation ID
 * @returns React Query result with single lookup_designation data
 */
export const useLookupDesignations = (id: string) => {
  const client = useLookupDesignationsClient();
  
  return useQuery({
    queryKey: lookupDesignationsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_designations
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_designations_business_hooks/
 */

/**
 * Hook to create a new lookup_designation
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, code, name, kind, sort_order, archived, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_designation
 */
export const useCreateLookupDesignations = () => {
  const client = useLookupDesignationsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupDesignationsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupDesignationsKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_designation
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_designation
 */
export const useUpdateLookupDesignations = () => {
  const client = useLookupDesignationsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupDesignationsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupDesignationsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupDesignationsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_designation
 *
 * @returns React Query mutation for deleting lookup_designation
 */
export const useDeleteLookupDesignations = () => {
  const client = useLookupDesignationsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupDesignationsKeys.all });
    },
  });
};

/**
 * Hook to count lookup_designations with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, code, kind
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupDesignationsCount = (filter?: Partial<Record<typeof LookupDesignationsFieldConfig.filterable[number], any>>) => {
  const client = useLookupDesignationsClient();
  
  return useQuery({
    queryKey: lookupDesignationsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupDesignationsFieldConfig = () => {
  return LookupDesignationsFieldConfig;
};